import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import i18n from 'i18next';
import { useTheme } from '@material-ui/core';
import useStyles from './FindingCharts.styles';
import { adminOnlyLabelFiltersEnum } from '../../models/findingLabelFilterEnum';
import getFilteredFindings from '../../../patrol/utils/getFilteredFindings';
import { allFindingTypeFiltersEnum, generalFindingTypeFiltersEnum } from '../../models/findingTypeFilterEnum';
import { FindingShape } from '../../models/Finding';
import { ContainerLoader } from '../../../common/components/loaders';
import { ClientCategoryShape } from '../../../client/models/ClientCategory';
import enTranslations from '../../__i18n__/en';
import frTranslations from '../../__i18n__/fr';

const FindingCharts = ({ findings = [], isFetchingFindings, onlyShowClientLabels, clientCategories }) => {
  const classes = useStyles();
  const theme = useTheme();

  const combinedCategories = useMemo(() => {
    const categories = [...(clientCategories || [])];

    if (!onlyShowClientLabels) {
      const adminCategories = Object.keys(adminOnlyLabelFiltersEnum).map((key) => ({
        key: adminOnlyLabelFiltersEnum[key],
        labelEn: enTranslations.class?.[key],
        labelFr: frTranslations.class?.[key],
      }));

      categories.push(...adminCategories);
    }

    return categories;
  }, [clientCategories, onlyShowClientLabels]);

  const classData = useMemo(
    () =>
      combinedCategories.map((category) => {
        const label = i18n.language === 'fr' ? category.labelFr : category.labelEn;
        return {
          name: category.key,
          label,
          value: getFilteredFindings(findings, { findingType: [], findingClass: [category.key].flat() }, clientCategories).length,
          color: theme.palette.common.black,
        };
      }),
    [combinedCategories, findings, theme.palette.common.black, clientCategories]
  );

  const typeData = useMemo(() => {
    const types = onlyShowClientLabels ? generalFindingTypeFiltersEnum : allFindingTypeFiltersEnum;
    return Object.keys(types).map((key) => ({
      name: key,
      label: i18n.t(`finding.type.${key}`),
      value: getFilteredFindings(findings, { findingType: [types[key]], findingClass: [] }).length,
      color: theme.palette.findingType[key],
    }));
  }, [onlyShowClientLabels, findings, theme.palette.findingType]);

  return (
    <>
      {isFetchingFindings && (
        <div className={classes.root}>
          <ContainerLoader />
        </div>
      )}
      <div id="findingCharts" className={classes.root}>
        <div className={classes.container}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={classData} layout="vertical" margin={{ top: 0, right: 35, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="label" width={215} interval={0} />
              <Bar dataKey="value" label={{ position: 'right' }}>
                {classData.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className={classes.container}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={typeData} layout="vertical" margin={{ top: 0, right: 35, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="label" width={160} />
              <Bar dataKey="value" label={{ position: 'right' }}>
                {typeData.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
};

FindingCharts.propTypes = {
  onlyShowClientLabels: PropTypes.bool.isRequired,
  findings: PropTypes.arrayOf(FindingShape).isRequired,
  isFetchingFindings: PropTypes.bool.isRequired,
  clientCategories: PropTypes.arrayOf(ClientCategoryShape).isRequired,
};

FindingCharts.defaultProps = {};

export default FindingCharts;
